import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import './css/Menu.css'

import { TALK_TO_US_URL, BOOK_DEMO_LABEL } from './contact'

// The mark alone, at every width: it is the home button, so the bar carries
// no "Home" link of its own. The light version of the logo, for the dark bar.
const logoSrc: string = require( './images/logos/Logo.002.Dark.png' )

///@brief   Distance in px between the bar's underside and the top of the
///         page's first content (what the page marks with data-menu-veil:
///         the hero's title, the trial page's mark) at which the bar starts
///         filling with black. Where that content rests closer to the bar
///         than this, as on a phone, the fill starts from where it rests
///         instead, so the bar is always clear on arrival.
const VEIL_START_GAP: number = 150
///@brief   Distance at which it is fully opaque, a hair before they touch.
const VEIL_END_GAP: number = 16

export function MenuElement() : React.JSX.Element {

	// Below 960px the links live in a panel opened by a menu button.
	const [ bOpen, setOpen ] = React.useState<boolean>( false )

	// Close the panel whenever the visitor lands somewhere.
	const location = useLocation()
	React.useEffect( (): void => {
		setOpen( false )
	}, [ location ] )

	// Over a page that marks its first content with data-menu-veil (the
	// hero, the trial page) the bar carries no background of its own, so the
	// page's drifting light runs up through it. As that content rises to
	// meet the bar the black fades in underneath, until the bar is opaque by
	// the time the two would touch, and the menu's own words never end up
	// printed over it. The veil is written straight to the element as a CSS
	// variable rather than held in state, so a scroll costs no render.
	const barRef = React.useRef<HTMLDivElement>( null )

	React.useLayoutEffect( (): ( () => void ) => {
		const update = (): void => {
			const bar: HTMLDivElement | null = barRef.current
			if ( bar === null ) return

			// Nothing marked on this page: nothing to show through, so the
			// bar is opaque from the start.
			const anchor: Element | null = document.querySelector( '[data-menu-veil]' )
			let veil: number = 1

			if ( anchor !== null ) {
				const gap: number = anchor.getBoundingClientRect().top - bar.offsetHeight
				// Where the content rests with the page scrolled to its top:
				// it does not move with the scroll, the viewport does.
				const restingGap: number = gap + window.scrollY
				const start: number = Math.min( VEIL_START_GAP, restingGap )
				// Content resting right under the bar leaves no room to fade
				// over: the bar is still clear at rest, and opaque a pixel on.
				const span: number = Math.max( start - VEIL_END_GAP, 1 )
				veil = ( start - gap ) / span
				veil = veil < 0 ? 0 : veil > 1 ? 1 : veil
			}

			bar.style.setProperty( '--menu-veil', veil.toFixed( 3 ) )
		}

		update()
		window.addEventListener( 'scroll', update, { passive: true } )
		window.addEventListener( 'resize', update )

		return (): void => {
			window.removeEventListener( 'scroll', update )
			window.removeEventListener( 'resize', update )
		}
	}, [ location ] )

	// Open, the panel closes on a tap anywhere outside the bar, or on Escape.
	// On a phone the backdrop over the page catches the tap first, and closes
	// it the same way.
	React.useEffect( (): ( () => void ) | undefined => {
		if ( !bOpen ) return undefined

		const onPointerDown = ( e: PointerEvent ): void => {
			const bar: HTMLDivElement | null = barRef.current
			if ( bar !== null && !bar.contains( e.target as Node ) ) setOpen( false )
		}
		const onKeyDown = ( e: KeyboardEvent ): void => {
			if ( e.key === 'Escape' ) setOpen( false )
		}

		document.addEventListener( 'pointerdown', onPointerDown )
		document.addEventListener( 'keydown', onKeyDown )

		return (): void => {
			document.removeEventListener( 'pointerdown', onPointerDown )
			document.removeEventListener( 'keydown', onKeyDown )
		}
	}, [ bOpen ] )

	return (
		<div ref={ barRef } className={ `Menu${ bOpen ? ' Menu-Open' : '' }` }>
			{ /* Under the panel, over the page: dims it while the panel is
			     open, and a tap on it closes the panel. */ }
			{ bOpen && <div className="Menu-Backdrop" onClick={ (): void => setOpen( false ) } aria-hidden="true" /> }

			<div className="Container">
				<Link to="/" className="Logo" aria-label="underlayer home" >
					<img className="Logo-Image" src={ logoSrc } alt="underlayer" />
				</Link>

				<button
					type="button"
					className="Menu-Toggle"
					aria-label={ bOpen ? 'Close menu' : 'Open menu' }
					aria-expanded={ bOpen }
					onClick={ (): void => setOpen( !bOpen ) }
				>
					<span className="Menu-Toggle-Bar" />
					<span className="Menu-Toggle-Bar" />
					<span className="Menu-Toggle-Bar" />
				</button>

				{ /* Any link closes the panel, the external one included, which
				     no location change would otherwise catch. */ }
				<nav className="Menu-Nav" onClick={ (): void => setOpen( false ) } >
					{ /* The pages, right after the logo. */ }
					<div className="Menu-Links" >
						<NavLink to="/technology" className="Menu-Link" >Technology</NavLink>
						<NavLink to="/performances" className="Menu-Link" >Performances</NavLink>
						<NavLink to="/pricing" className="Menu-Link" >Pricing</NavLink>
						<NavLink to="/faq" className="Menu-Link" >FAQ</NavLink>
					</div>

					{ /* Contact and the demo booking, pushed to the right. */ }
					<div className="Menu-Actions" >
						<NavLink to="/contact" className="Menu-Link" >Contact</NavLink>
						<a
							href={ TALK_TO_US_URL }
							target="_blank"
							rel="noopener noreferrer"
							className="Menu-Cta"
						>{ BOOK_DEMO_LABEL }</a>
					</div>
				</nav>
			</div>
		</div>
	);
}
